import { useEffect, useRef, useCallback } from 'react';
import {
  createChart,
  createSeriesMarkers,
  CandlestickSeries,
  HistogramSeries,
  ColorType,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { CandleData, Timeframe } from '../types/gateway';
import { TIMEFRAMES, INFO_BAR_TIMEFRAMES, AVAILABLE_TIMEFRAMES } from '../types/gateway';
import { useTerminalStore } from '../store/useTerminalStore';
import { useTranslation } from '../i18n/useTranslation';

interface FillMarker {
  time: number;
  side: 'BUY' | 'SELL';
  price: number;
  label?: string;
}

interface TradingViewChartProps {
  candles: CandleData[];
  fills?: FillMarker[];
  timeframe?: Timeframe;
  onTimeframeChange?: (tf: Timeframe) => void;
  className?: string;
}

const UP_COLOR = '#01E599';
const DOWN_COLOR = '#FF3366';
const GRID_COLOR = '#1E293B';
const TEXT_COLOR = '#7A8C9E';

export default function TradingViewChart({
  candles,
  fills,
  timeframe = '4H',
  onTimeframeChange,
  className,
}: TradingViewChartProps) {
  const { t } = useTranslation();
  const dataSource = useTerminalStore((s) => s.dataSource);
  const symbol = useTerminalStore((s) => s.selectedSymbol);
  const lastPrice = useTerminalStore((s) => s.instruments[s.selectedSymbol]?.last_price);

  const barsIsFallback = useTerminalStore((s) => s.barsIsFallback);

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const markersPluginRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  // Guard against React 19 StrictMode double-invocation
  const initializedRef = useRef(false);

  const initChart = useCallback(() => {
    const container = containerRef.current;
    if (!container || initializedRef.current) return;

    initializedRef.current = true;

    const chart = createChart(container, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: TEXT_COLOR,
      },
      grid: {
        vertLines: { color: GRID_COLOR },
        horzLines: { color: GRID_COLOR },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: GRID_COLOR },
      timeScale: {
        borderColor: GRID_COLOR,
        timeVisible: true,
        secondsVisible: false,
      },
      width: container.clientWidth,
      height: container.clientHeight,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: UP_COLOR,
      downColor: DOWN_COLOR,
      borderUpColor: UP_COLOR,
      borderDownColor: DOWN_COLOR,
      wickUpColor: UP_COLOR,
      wickDownColor: DOWN_COLOR,
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });

    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 },
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry && chartRef.current) {
        const { width, height } = entry.contentRect;
        chartRef.current.applyOptions({ width, height });
      }
    });

    observer.observe(container);
    resizeObserverRef.current = observer;
  }, []);

  // Initialize chart on mount
  useEffect(() => {
    initChart();

    return () => {
      // Cleanup markers before chart
      if (markersPluginRef.current) {
        markersPluginRef.current.detach();
        markersPluginRef.current = null;
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }

      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      initializedRef.current = false;
    };
  }, [initChart]);

  // Update candle + volume data
  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    const volumeSeries = volumeSeriesRef.current;
    const chart = chartRef.current;

    if (!candleSeries || !volumeSeries || !chart) return;

    const sortedCandles = [...candles].sort((a, b) => a.time - b.time);

    candleSeries.setData(
      sortedCandles.map((c) => ({
        time: c.time as UTCTimestamp,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      }))
    );

    volumeSeries.setData(
      sortedCandles.map((c) => ({
        time: c.time as UTCTimestamp,
        value: c.volume,
        color: c.close >= c.open ? `${UP_COLOR}80` : `${DOWN_COLOR}80`,
      }))
    );

    if (sortedCandles.length > 0) {
      chart.timeScale().fitContent();
    }
  }, [candles]);

  // Update fill markers
  useEffect(() => {
    const candleSeries = candleSeriesRef.current;
    if (!candleSeries) return;

    // Detach previous markers plugin before creating new one
    if (markersPluginRef.current) {
      markersPluginRef.current.detach();
      markersPluginRef.current = null;
    }

    if (!fills || fills.length === 0) return;

    const markerData = fills.map((fill) => ({
      time: fill.time as UTCTimestamp,
      position: fill.side === 'BUY' ? ('belowBar' as const) : ('aboveBar' as const),
      color: fill.side === 'BUY' ? UP_COLOR : DOWN_COLOR,
      shape: fill.side === 'BUY' ? ('arrowUp' as const) : ('arrowDown' as const),
      text: fill.label ?? fill.side,
    }));

    markersPluginRef.current = createSeriesMarkers(candleSeries, markerData);
  }, [fills]);

  const handleTimeframeClick = useCallback(
    (tf: Timeframe) => {
      onTimeframeChange?.(tf);
    },
    [onTimeframeChange]
  );

  return (
    <div
      className={`relative flex flex-col h-full w-full${className ? ` ${className}` : ''}`}
      data-testid="bar-chart"
    >
      {/* Timeframe switcher */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
        {TIMEFRAMES.map((tf, i) => {
          const isInfoBar = INFO_BAR_TIMEFRAMES.has(tf);
          const isActive = timeframe === tf;
          // Insert separator at the boundary between information bars and time bars
          const prevIsInfoBar = i > 0 && INFO_BAR_TIMEFRAMES.has(TIMEFRAMES[i - 1]);
          const showSeparator = prevIsInfoBar && !isInfoBar;
          return (
            <div key={tf} className="flex items-center gap-1">
              {showSeparator && (
                <span className="border-r border-border mx-1 self-stretch" aria-hidden="true" />
              )}
              <button
                onClick={() => handleTimeframeClick(tf)}
                aria-pressed={isActive}
                title={isInfoBar ? 'Information bar' : undefined}
                data-testid={`timeframe-${tf}`}
                className={`px-2 py-0.5 text-[10px] font-mono rounded-sm border transition-colors ${
                  isActive
                    ? 'bg-primary/15 text-primary border-primary/30'
                    : isInfoBar
                      ? 'text-primary/70 border-border hover:text-primary hover:bg-card-elevated'
                    : !AVAILABLE_TIMEFRAMES.has(tf)
                      ? 'text-muted-foreground/30 border-border/30 cursor-help'
                      : 'text-muted-foreground border-border hover:text-foreground hover:bg-card-elevated'
                }`}
              >
                {tf}
              </button>
            </div>
          );
        })}
      </div>

      {/* 1m-fallback badge — shown when dollar bars are absent for this symbol.
          Beta users must know the chart is on 1m proxy, not the native dollar timeframe.
          Disappears automatically once dollar bars exist (barsIsFallback resets to false). */}
      {barsIsFallback && (
        <div
          data-testid="chart-1m-fallback-badge"
          className="absolute top-2 left-2 z-10 flex items-center gap-1"
        >
          <span className="px-1.5 py-0.5 text-[9px] font-mono rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-400/80 select-none">
            1m proxy
          </span>
        </div>
      )}

      {/* Chart container */}
      <div className="flex-1 w-full relative">
        <div
          ref={containerRef}
          className="absolute inset-0"
          aria-label="TradingView candlestick chart"
          role="img"
          data-testid="bar-chart-canvas-root"
        />
        {candles.length === 0 && (
          dataSource === 'degraded' ? (
            // Gateway bootstrap failed — the global DegradedDataBanner (DDD #180 S1)
            // already signals this. Keep the chart note muted, no awaiting-bars testid.
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <span className="text-[10px] font-mono text-muted-foreground">
                {t('chart.liveUnavailable')}
              </span>
            </div>
          ) : (
            // Empty array from a healthy gateway = no OHLCV rows yet for this symbol
            // (new instrument · bar pipeline still accruing). Informative, not "broken".
            <div
              data-testid="chart-awaiting-bars"
              className="absolute inset-0 flex flex-col items-center justify-center gap-1 z-10 px-6 text-center"
            >
              <span className="text-[10px] font-mono text-muted-foreground">
                {t('chart.awaitingBars').replace('{symbol}', symbol)}
              </span>
              {lastPrice != null && (
                <span className="text-[10px] font-mono text-foreground/70">
                  {t('chart.livePrice').replace(
                    '{price}',
                    lastPrice.toLocaleString(undefined, { maximumFractionDigits: 6 }),
                  )}
                </span>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}
